import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Eye, Trash2 } from "lucide-react";
import { toast } from "react-toastify";
import { AdminLayout } from "../../layout/AdminLayout";
import { DataTable } from "../../../components/DataTable";
import { inquiriesService } from "../../../services/inquiriesService";

export const InquiriesList = () => {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");

  useEffect(() => {
    loadInquiries();
  }, [page, pageSize, search, sortBy, sortOrder]);

  const loadInquiries = async () => {
    try {
      setLoading(true);
      const data = await inquiriesService.getAll(
        page,
        pageSize,
        search,
        sortBy,
        sortOrder,
      );
      setInquiries(data.data || []);
      setTotal(data.total || 0);
    } catch {
      toast.error("Failed to load inquiry entries");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (
      !window.confirm("Are you sure you want to delete this inquiry entry?")
    ) {
      return;
    }

    try {
      await inquiriesService.delete(id);
      toast.success("Inquiry entry deleted");
      loadInquiries();
    } catch {
      toast.error("Failed to delete inquiry entry");
    }
  };

  const columns = [
    {
      key: "name",
      label: "Name",
      sortable: true,
      width: "17%",
    },
    {
      key: "email",
      label: "Email",
      sortable: true,
      width: "22%",
    },
    {
      key: "company",
      label: "Company",
      sortable: true,
      width: "16%",
    },
    {
      key: "resources_count",
      label: "Resources",
      sortable: false,
      width: "10%",
    },
    {
      key: "message",
      label: "Message",
      sortable: false,
      width: "19%",
      render: (row) => (
        <span className="line-clamp-2" title={row.message}>
          {row.message}
        </span>
      ),
    },
    {
      key: "createdAt",
      label: "Submitted",
      sortable: true,
      width: "8%",
      render: (row) => new Date(row.createdAt).toLocaleDateString(),
    },
    {
      key: "actions",
      label: "Actions",
      sortable: false,
      width: "8%",
      render: (row) => (
        <div className="flex gap-2">
          <Link
            to={`/admin/inquiries/${row._id}`}
            className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
          >
            <Eye size={16} />
            View
          </Link>
          <button
            onClick={() => handleDelete(row._id)}
            className="text-red-600 hover:text-red-800 flex items-center gap-1"
          >
            <Trash2 size={16} />
            Delete
          </button>
        </div>
      ),
    },
  ];

  return (
    <AdminLayout>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-800">Inquiry Entries</h1>
        </div>

        <DataTable
          columns={columns}
          data={inquiries}
          loading={loading}
          totalItems={total}
          currentPage={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          onSearch={setSearch}
          onSort={(column, order) => {
            setSortBy(column);
            setSortOrder(order);
          }}
          searchPlaceholder="Search by name, email, company, or message..."
          sortBy={sortBy}
          sortOrder={sortOrder}
        />
      </div>
    </AdminLayout>
  );
};
